//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnModel } from '../../../api/api.models'
import { Enums } from '../../../../models/common'
import { GoAlarmDef } from '../alarm-definitions-edit/interfaces/alarm-definitions-edit.interfaces'
import { OpAlarmDef, OpAlarmType, OpDataType } from '../../../../models/core'
import { AlarmDefinitionService } from '../../../../services/alarm-definition'
import { LocalizationHelper, LogHelper, TypeHelper } from '../../../../helpers'
import { UserSessionService } from '../../../../services/user-session'
import { IAlarmDefinitionItemEdit, AlarmDefinitionItemEditOptions } from './alarm-definition-item-edit.interfaces'

export class AlarmDefinitionItemEditController
    implements ng.IComponentController
    , IAlarmDefinitionItemEdit {

    // #region consts

    public get ALARM_DEF_SELECT_TITLE_TEXT(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefSelectTitle) }
    public get ALARM_DEF_SELECT_TYPE_TEXT(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefSelectType) }
    public get ALARM_DEF_REF_TYPE_TEXT(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefRefType) }
    public get ALARM_DEF_REF_VALUE_TEXT(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefRefValue) }
    public get ACTIVE_TEXT(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active) }

    private static get ValuePropertyName(): string { return 'value' }
    private static get IdPropertyName(): string { return 'id' }
    private static get CaptionPropertyName(): string { return 'caption' }
    private static get IdAlarmTypePropertyName(): string { return 'idAlarmType' }
    private static readonly CustomPropertyName = 'custom'

    private static get PopupCollectionName(): string { return 'Popup' }
    private static get TrashIconName(): string { return 'trash' }

    // #endregion

    // #region Members

    private originalAlarmDef: GoAlarmDef

    private isCustomDataVisible: boolean

    private options: AlarmDefinitionItemEditOptions

    private deleteButtonOptions: DevExpress.ui.dxButtonOptions

    private alarmDefValidationGroup: DevExpress.ui.dxValidationGroup
    private alarmDefValidationGroupOptions: DevExpress.ui.dxValidationGroupOptions

    private alarmDefDataTypeAlarmSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    private alarmDefDataTypeAlarmSelectBoxValidatorOptions: DevExpress.ui.dxValidatorOptions

    private alarmDefConditionSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    private alarmDefConditionValidatorOptions: DevExpress.ui.dxValidatorOptions

    private alarmDefRefValueSelectBox: DevExpress.ui.dxSelectBox
    private alarmDefRefValueSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions

    private alarmDefRefValueValidator: DevExpress.ui.dxValidator
    private alarmDefRefValueValidatorOptions: DevExpress.ui.dxValidatorOptions

    private alarmDefReferenceValueTypeSwitch: DevExpress.ui.dxSwitch
    private alarmDefReferenceValueTypeSwitchOptions: DevExpress.ui.dxSwitchOptions

    private alarmDefReferenceValueTextBox: DevExpress.ui.dxTextBox
    private alarmDefReferenceValueTextBoxOptions: DevExpress.ui.dxTextBoxOptions
    private alarmDefReferenceValueTextBoxValidator: DevExpress.ui.dxValidatorOptions

    private alarmDefReferenceValueSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    private alarmDefReferenceValueSelectBoxValidator: DevExpress.ui.dxValidatorOptions

    private alarmDefIsActiveSwitch: DevExpress.ui.dxSwitch
    private alarmDefIsActiveSwitchOptions: DevExpress.ui.dxSwitchOptions

    // #endregion

    // #region ctor

    public constructor(private userSessionService: UserSessionService) {
        this.isCustomDataVisible = false
    }

    // #endregion

    // #region ng.IComponentController

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region IAlarmDefinitionItemEdit

    public isAlarmDefCustomDataVisible(): boolean {
        return this.isCustomDataVisible
    }

    public setAlarmDefCustomDataVisible(value: boolean): void {
        this.isCustomDataVisible = value
    }

    public validateAlarmDefData(): boolean {
        if (this.alarmDefValidationGroup != null && this.isAlarmDefVisible() && !this.isDelete()) {
            const validationResult: DevExpress.ui.dxValidationGroupResult = this.alarmDefValidationGroup.validate()

            if (validationResult != null) {
                return validationResult.isValid
            }
        }

        return true
    }

    // #endregion

    // #region Setup

    private initialize(): void {

        const currentAlarmDef: GoAlarmDef = this.alarmDefExists()
            ? this.options.alarmDef
            : new GoAlarmDef(new OpAlarmDef(null))

        this.originalAlarmDef = currentAlarmDef.clone(true, true, true, true)

        this.options.alarmDef.alarmDef.dataTypeAlarm = this.ensureDataType(null, this.options.alarmDef.alarmDef.idDataTypeAlarm)

        this.deleteButtonOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefDelete, { collection: AlarmDefinitionItemEditController.PopupCollectionName }),
            icon: AlarmDefinitionItemEditController.TrashIconName,
            onClick: () => { this.onAlarmDefDeleteClick() }
        }

        this.alarmDefValidationGroupOptions = {
            onInitialized: (args: any) => { this.onAlarmDefValidationGroupInitialized(args) }
        }

        this.alarmDefDataTypeAlarmSelectBoxOptions = {
            placeholder: currentAlarmDef.alarmDef.dataTypeAlarm != null && !TypeHelper.isNullOrEmpty(currentAlarmDef.alarmDef.dataTypeAlarm.toString())
                ? currentAlarmDef.alarmDef.dataTypeAlarm.toString()
                : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

            value: currentAlarmDef.alarmDef.idDataTypeAlarm,
            readOnly: currentAlarmDef.isTemplate || currentAlarmDef.idAlarmDef != 0,

            dataSource: this.options.alarmDefParams,

            displayExpr: AlarmDefinitionItemEditController.CaptionPropertyName,
            valueExpr: AlarmDefinitionItemEditController.IdPropertyName,

            noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),

            onValueChanged: (args: any) => { this.onAlarmDefDataTypeAlarmChanged(args) },
        }

        this.alarmDefDataTypeAlarmSelectBoxValidatorOptions = {
            validationRules: [this.createValidationRuleRequired(true)]
        }

        this.alarmDefConditionSelectBoxOptions = {
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

            value: currentAlarmDef.alarmDef.idAlarmType,
            readOnly: currentAlarmDef.isTemplate || currentAlarmDef.idAlarmDef != 0,

            dataSource: this.options.alarmTypes,

            displayExpr: (arg: OpAlarmType) => { return arg == null ? TypeHelper.emptyString : arg.toString() },
            valueExpr: AlarmDefinitionItemEditController.IdAlarmTypePropertyName,

            noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),

            onValueChanged: (args: any) => { this.onAlarmDefConditionChanged(args) },
        }

        this.alarmDefConditionValidatorOptions = {
            validationRules: [this.createValidationRuleRequired()]
        }

        this.alarmDefRefValueSelectBoxOptions = {
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

            disabled: currentAlarmDef.alarmDef.readOnly,

            dataSource: this.options.alarmDefRefParams,

            displayExpr: AlarmDefinitionItemEditController.CaptionPropertyName,
            valueExpr: AlarmDefinitionItemEditController.IdPropertyName,

            noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),

            bindingOptions: { value: '$ctrl.options.alarmDef.idDataTypeConfig' },

            onInitialized: (args: any) => { this.alarmDefRefValueSelectBox = args.component },
            onValueChanged: (args: any) => { this.onAlarmDefRefValueChanged(args) }
        }

        this.alarmDefRefValueValidatorOptions = {
            bindingOptions: { disabled: '$ctrl.options.alarmDef.readOnly' },

            validationRules: [
                this.createValidationRuleRequired(),
                {
                    type: AlarmDefinitionItemEditController.CustomPropertyName,
                    reevaluate: true,
                    message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField),
                    validationCallback: (options: any) => { return this.validateAlarmDefRefValue(options) }
                }
            ],

            onInitialized: (args: any) => { this.alarmDefRefValueValidator = args.component }
        }

        this.alarmDefReferenceValueTypeSwitchOptions = {
            value: currentAlarmDef.alarmDef.systemAlarmValue != null && typeof currentAlarmDef.alarmDef.systemAlarmValue != 'undefined',

            disabled: currentAlarmDef.alarmDef.readOnly,

            onText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefRefTypeOwn, { collection: AlarmDefinitionItemEditController.PopupCollectionName }),
            offText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmDefRefTypeList, { collection: AlarmDefinitionItemEditController.PopupCollectionName }),

            width: '80%',

            onInitialized: (args: any) => { this.onAlamrDefReferenceValueTypeSwitchInitialized(args) },
            onValueChanged: (args: any) => { this.onAlarmDefReferenceValueTypeSwitchChanged(args) }
        }

        if (!this.isAlarmDefReferenceValueBoolean()) {
            this.alarmDefReferenceValueTextBoxOptions = {
                value: currentAlarmDef.alarmDef.systemAlarmValue,

                disabled: currentAlarmDef.alarmDef.readOnly,

                onKeyDown: (args: any) => { this.onAlarmDefUpdated() },

                onInitialized: (args: any) => {
                    this.alarmDefReferenceValueTextBox = args.component
                },

                onValueChanged: (args: any) => { this.onAlarmDefReferenceValueCustomChanged(args) }
            }

            this.alarmDefReferenceValueTextBoxValidator = {
                validationRules: [
                    this.createValidationRuleRequired(),
                    {
                        type: AlarmDefinitionItemEditController.CustomPropertyName,
                        reevaluate: true,
                        validationCallback: (options: any) => { return this.validateAlarmDefReferenceValue(options) }
                    }
                ]
            }
        } else {

            this.alarmDefReferenceValueSelectBoxOptions = {
                placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

                value: (typeof currentAlarmDef.alarmDef.systemAlarmValue != 'undefined' && currentAlarmDef.alarmDef.systemAlarmValue != null
                    ? currentAlarmDef.alarmDef.systemAlarmValue.toString().toLowerCase() == 'true' // TO ma smutny sens, jeśli nie jesteśmy pewni czy przyjdzie nam boolean lub wartość castowalna na boolean
                    : undefined
                ),

                disabled: currentAlarmDef.alarmDef.readOnly,

                dataSource: [
                    {
                        value: true,
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.True)
                    },
                    {
                        value: false,
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.False)
                    }
                ],

                valueExpr: AlarmDefinitionItemEditController.ValuePropertyName,
                displayExpr: 'text',

                onValueChanged: (args: any) => { this.onAlarmDefReferenceValueSelectionChanged(args) },
            }

            this.alarmDefReferenceValueSelectBoxValidator = {
                validationRules: [{
                    type: AlarmDefinitionItemEditController.CustomPropertyName,
                    message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField),
                    validationCallback: (options) => { return options != null && options.value != null }
                }],
            }
        }

        this.alarmDefIsActiveSwitchOptions = {
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active),
            value: currentAlarmDef.alarmDef.isEnabled,
            onValueChanged: (args: any) => { this.onAlarmDefIsActiveChanged(args) },
            onInitialized: (args: any) => { this.onAlarmDefIsActiveSwitchInitialized(args) }
        }

        this.onInitialized()
    }

    // #endregion

    // #region Events

    // #region AlarmDefinitionItemEditController

    private onInitialized(): void {
        if (this.options != null && this.options.onInitialized != null) {
            this.options.onInitialized(this)
        }
    }

    // #endregion

    // #region deleteButton

    private onAlarmDefDeleteClick(): void {
        if (!this.alarmDefExists()) { return }

        this.options.alarmDef.opState = Enums.OpChangeState.Delete

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefValidationGroup

    private onAlarmDefValidationGroupInitialized(args: any): void {
        this.alarmDefValidationGroup = args.component
    }

    // #endregion

    // #region alarmDefDataTypeAlarmSelectBox

    private onAlarmDefDataTypeAlarmChanged(args: any): void {
        if (!this.alarmDefExists() || args == null) { return }

        this.options.alarmDef.alarmDef.dataTypeAlarm = this.ensureDataType(null, args.value)

        // Jeśli jakimś cudem nie znajdziemy datatypu w słowniku...
        if (this.options.alarmDef.alarmDef.dataTypeAlarm.idDataTypeClass == Enums.DataTypeClass.Unknown) {

            const dataTypeColumn: ColumnModel = _.find(this.options.alarmDefParams, (column: ColumnModel) => column.id == this.options.alarmDef.alarmDef.idDataTypeAlarm)

            if (dataTypeColumn != null) {
                this.options.alarmDef.alarmDef.dataTypeAlarm.idDataTypeClass = dataTypeColumn.type
            }
        }

        this.options.alarmDef.alarmDef.idDataTypeAlarm = this.options.alarmDef.alarmDef.dataTypeAlarm.idDataType

        this.markAsModified(this.options.alarmDef)

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefConditionSelectBox

    private onAlarmDefConditionChanged(args: any): void {
        if (!this.alarmDefExists() || args == null) { return }

        this.options.alarmDef.alarmDef.idAlarmType = args.value

        this.markAsModified(this.options.alarmDef)

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefRefValueSelectBox

    private onAlarmDefRefValueChanged(args: any): void {
        if (!this.alarmDefExists() || !this.alarmDefReferenceValueTypeSwitchExists()) { return }

        if (!this.isAlarmDefReferenceValueTypeCustomValue()) {
            this.options.alarmDef.alarmDef.dataTypeConfig = new OpDataType(this.ensureDataType(null, args.value))
            this.options.alarmDef.alarmDef.idDataTypeConfig = args.value
        }

        this.markAsModified(this.options.alarmDef)

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefIsActiveSwitch

    private onAlarmDefIsActiveSwitchInitialized(args: any): void {
        this.alarmDefIsActiveSwitch = args.component

        if (!this.alarmDefExists() || this.alarmDefIsActiveSwitch == null) { return }

        this.alarmDefIsActiveSwitch.option(AlarmDefinitionItemEditController.ValuePropertyName, this.options.alarmDef.alarmDef.isEnabled)

        setTimeout(() => {
            this.alarmDefIsActiveSwitch.repaint()
        })
    }

    private onAlarmDefIsActiveChanged(args: any): void {
        if (!this.alarmDefExists()) { return }

        this.options.alarmDef.alarmDef.isEnabled = args.value

        this.markAsModified(this.options.alarmDef)

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefReferenceValueSelectBox

    private onAlarmDefReferenceValueSelectionChanged(args: any): void {
        if (!this.alarmDefExists() || !this.alarmDefReferenceValueTypeSwitchExists()) { return }

        if (this.alarmDefReferenceValueTypeSwitch.option(AlarmDefinitionItemEditController.ValuePropertyName) === true) {
            this.options.alarmDef.alarmDef.idDataTypeConfig = null
            this.options.alarmDef.alarmDef.dataTypeConfig = null
            this.options.alarmDef.alarmDef.systemAlarmValue = args.value

            this.markAsModified(this.options.alarmDef)
        }

        this.onAlarmDefUpdated()
    }

    // #endregion

    // #region alarmDefReferenceValueTextBox

    private onAlarmDefReferenceValueCustomChanged(args: any): any {
        if (!this.alarmDefExists()) { return }

        if (this.alarmDefReferenceValueTypeSwitch.option(AlarmDefinitionItemEditController.ValuePropertyName) === true) {
            let value: any = typeof args.value === 'string'
                ? args.value.replace(',', '.')
                : args.value

            this.options.alarmDef.alarmDef.dataTypeAlarm = this.ensureDataType(this.options.alarmDef.alarmDef.dataTypeAlarm, this.options.alarmDef.alarmDef.idDataTypeAlarm)

            switch (this.options.alarmDef.alarmDef.dataTypeAlarm.idDataTypeClass) {
                case Enums.DataTypeClass.Integer:
                case Enums.DataTypeClass.Real:
                case Enums.DataTypeClass.Decimal:
                case Enums.DataTypeClass.Binary:
                    value = +value
                    break
            }

            this.options.alarmDef.alarmDef.systemAlarmValue = value
        }

        if (args.value != args.previousValue) {
            this.markAsModified(this.options.alarmDef)

            this.onAlarmDefUpdated()
        }
    }

    // #endregion

    // #region alarmDefReferenceValueTypeSwitch

    private onAlamrDefReferenceValueTypeSwitchInitialized(args: any): any {
        if (!this.alarmDefExists()) { return }

        this.alarmDefReferenceValueTypeSwitch = args.component

        const switchValue: boolean = this.alarmDefExists() && this.options.alarmDefRefParams != null && this.options.alarmDefRefParams.length > 0
            ? (!(!this.options.alarmDef.alarmDef.systemAlarmValue))
            : true

        this.alarmDefReferenceValueTypeSwitch.option(AlarmDefinitionItemEditController.ValuePropertyName, switchValue)

        setTimeout(() => {
            this.alarmDefReferenceValueTypeSwitch.repaint()
        })
    }

    private onAlarmDefReferenceValueTypeSwitchChanged(args: any): any {
        if (!this.alarmDefExists()) { return }

        this.markAsModified(this.options.alarmDef)

        if (this.isAlarmDefReferenceValueTypeCustomValue()) {
            this.options.alarmDef.alarmDef.idDataTypeConfig = null
            this.options.alarmDef.alarmDef.dataTypeConfig = null

            if (this.alarmDefRefValueSelectBox != null) {
                this.alarmDefRefValueSelectBox.option(AlarmDefinitionItemEditController.ValuePropertyName, null)
            }

            if (this.isNew()) {
                this.options.alarmDef.alarmDef.systemAlarmValue = null
                this.alarmDefReferenceValueTextBox.option(AlarmDefinitionItemEditController.ValuePropertyName, null)
            }
            else {
                this.options.alarmDef.alarmDef.systemAlarmValue = this.originalAlarmDef.alarmDef.systemAlarmValue
                this.alarmDefReferenceValueTextBox.option(AlarmDefinitionItemEditController.ValuePropertyName, this.originalAlarmDef.alarmDef.systemAlarmValue)
            }
        }
        else {
            this.options.alarmDef.alarmDef.systemAlarmValue = null
            this.alarmDefReferenceValueTextBox.option(AlarmDefinitionItemEditController.ValuePropertyName, null)

            if (this.isNew()) {
                this.options.alarmDef.alarmDef.idDataTypeConfig = null
                this.options.alarmDef.alarmDef.dataTypeConfig = null

                if (this.alarmDefRefValueSelectBox != null) {
                    this.alarmDefRefValueSelectBox.option(AlarmDefinitionItemEditController.ValuePropertyName, null)
                }
            }
            else {
                this.options.alarmDef.alarmDef.idDataTypeConfig = this.originalAlarmDef.alarmDef.idDataTypeConfig
                this.options.alarmDef.alarmDef.dataTypeConfig = this.originalAlarmDef.alarmDef.dataTypeConfig

                if (this.alarmDefRefValueSelectBox != null) {
                    this.alarmDefRefValueSelectBox.option(AlarmDefinitionItemEditController.ValuePropertyName, this.originalAlarmDef.alarmDef.idDataTypeConfig)
                }
            }
        }

        this.onAlarmDefUpdated()
        this.alarmDefReferenceValueTypeSwitch.repaint()
    }

    // #endregion

    // #region alarmDefReferenceValueTextBox

    private onAlarmDefUpdated(): void {
        if (this.alarmDefExists() && this.options.alarmDefUpdated != null) {
            this.options.alarmDefUpdated(this.options.alarmDef)
        }
    }

    // #endregion

    // #region alarmDefReferenceValueTextBoxValidator


    private validateAlarmDefReferenceValue(options: any): boolean {
        if (!this.alarmDefExists() || this.options.alarmDef.alarmDef.dataTypeAlarm == null) { return false }

        switch (this.options.alarmDef.alarmDef.dataTypeAlarm.idDataTypeClass) {
            case Enums.DataTypeClass.Integer: {
                const pattern: RegExp = /^\d+$/

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeInteger))
            }
            case Enums.DataTypeClass.Real: {
                const pattern: RegExp = /^[-+]?[0-9]*(\.|,)?[0-9]+([eE][-+]?[0-9]+)?$/

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeReal))
            }
            case Enums.DataTypeClass.Decimal: {
                const pattern: RegExp = /^(?:\d*\.)?\d+$/

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeDecimal))
            }
            case Enums.DataTypeClass.Binary: {
                const pattern: RegExp = /^[0,1]+$/

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeBinary))
            }
            case Enums.DataTypeClass.Boolean: {
                const pattern: RegExp = /^[0,1]$|^\b[T,t]rue$|^\b[F,f]alse$|^[T,F,t,f]$/

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeBoolean))
            }
            case Enums.DataTypeClass.Date: {
                const pattern: RegExp = /^([12]{1}\d{3})((-{1}([0]{1}[1-9]?|[1]{1}[0-2]?)?)?(-{1}([0]{1}[1-9]?|[1-2]{1}\d?|[3]{1}[01]?)?)?)?$/g

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeDate))
            }
            case Enums.DataTypeClass.Time: {
                const pattern: RegExp = /^([0-1]\d|2[0-3])(?::([0-5]\d))?$/g

                return this.executeValueValidationByPattern(pattern, options, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeTime))
            }
            case Enums.DataTypeClass.Datetime: {
                const pattern: RegExp = /^([12]{1}\d{3})((-{1}([0]{1}[1-9]?|[1]{1}[0-2]?)?)?(-{1}([0]{1}[1-9]?|[1-2]{1}\d?|[3]{1}[01]?)?)?)? (([0-1]\d|2[0-3])(?::([0-5]\d)))$/g

                return this.executeValueValidationByPattern(pattern, options, `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeDatetime)}: YYYY-MM-DD HH:mm`)
            }
            case Enums.DataTypeClass.Text:
            case Enums.DataTypeClass.Xml: {
                if (options != null && !angular.isString(options.value)) {
                    options.rule.message = `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FieldOfType)}${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Type)}${this.getCustomReferenceTypeName()}`

                    return false
                }

                return true
            }
            default: {
                if (options != null && !angular.isDefined(options.value)) {
                    options.rule.message = `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FieldOfType)}${this.getCustomReferenceTypeName()}`

                    return false
                }

                return true
            }
        }
    }

    // #endregion

    // #region alarmDefRefValueValidator

    private validateAlarmDefRefValue(options: any): boolean {
        if (options != null) {
            const idDataType: number = new Number(options.value).valueOf()

            if (_.isFinite(idDataType) && this.alarmDefExists()) {
                if (idDataType === 0) {
                    return !(this.options.alarmDef.idAlarmDef != 0 || (this.options.alarmDef.isTemplate && this.options.alarmDef.idAlarmDef == 0))
                }

                return true
            }
        }

        return false
    }

    // #endregion

    // #endregion

    // #region AlarmDefinitionItemEditController

    public getName(): string {
        if (!this.alarmDefExists()) {
            return TypeHelper.emptyString
        }

        return this.options.alarmDef.name
    }

    public getAlarmDefUnitText(): string {
        if (!this.alarmDefUnitExists()) {
            return TypeHelper.emptyString
        }

        return this.options.alarmDef.alarmDef.dataTypeAlarm.unit.name
    }

    private executeValueValidationByPattern(pattern: RegExp, options: any, msg: any): boolean {
        if (pattern == null || options == null) { return false }

        const str = options.value == null
            ? null
            : options.value.toString()

        if (!pattern.test(str)) {
            options.rule.message = `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FieldOfType)}${msg}`

            return false
        }

        return true
    }

    private getCustomReferenceTypeName(): string {
        return this.alarmDefExists()
            && this.options.alarmDef.alarmDef.dataTypeAlarm != null
            && this.options.alarmDef.alarmDef.dataTypeAlarm.dataTypeClass != null
            ? this.options.alarmDef.alarmDef.dataTypeAlarm.dataTypeClass.name
            : TypeHelper.emptyString
    }

    private markAsModified(alarmDef: GoAlarmDef): void {
        if (alarmDef != null && alarmDef.idAlarmDef != 0) {
            alarmDef.opState = Enums.OpChangeState.Modified
        }
    }

    private ensureDataType(existingDataType: OpDataType, idDataType: number): OpDataType {
        if (existingDataType == null) {
            existingDataType = _.find(this.options.dataTypes, (dt: OpDataType) => dt.idDataType == idDataType)

            if (existingDataType == null) {
                LogHelper.warn(AlarmDefinitionService.LOG_PREFIX, `Failed to find datatype ${idDataType} in dictionary.`)

                existingDataType = new OpDataType(null)
                existingDataType.idDataType = idDataType
                existingDataType.idDataTypeClass = Enums.DataTypeClass.Unknown
            }
        }

        return existingDataType
    }

    // #endregion

    // #region Flags

    private isHeader(): boolean {
        return this.alarmDefExists()
            && this.options.alarmDef.idAlarmDef == 0
            && this.options.alarmDef.opState != Enums.OpChangeState.New
            && this.options.alarmDef.isTemplate
    }

    private isDeleteButtonVisible(): boolean {
        return this.alarmDefExists()
            && (
                this.options.alarmDef.idAlarmDef != 0
                || this.options.alarmDef.opState == Enums.OpChangeState.New
                || this.options.alarmDef.isTemplate
            )
    }

    private isNew(): boolean {
        return this.alarmDefExists()
            && (
                this.options.alarmDef.opState == Enums.OpChangeState.New
            )
    }

    private isModified(): boolean {
        return this.alarmDefExists()
            && (
                this.options.alarmDef.opState == Enums.OpChangeState.Modified
            )
    }

    private isDelete(): boolean {
        return this.alarmDefExists()
            && this.options.alarmDef.opState == Enums.OpChangeState.Delete
    }

    private alarmDefExists(): boolean {
        return this.options != null && this.options.alarmDef != null
    }

    private hasAlarmDefRefParams(): boolean {
        return this.options != null
            && this.options.alarmDefRefParams != null
            && this.options.alarmDefRefParams.length > 0
    }

    private isAlarmDefVisible(): boolean {
        return this.alarmDefExists()
            && this.options.alarmDef.alarmDef.visible
    }

    private isAlarmDefRefValuesVisible(): boolean {
        return this.isAlarmDefVisible()
            && !this.isAlarmDefReferenceValueTypeCustomValue()
    }

    private isAlarmDefRefValuesHidden(): boolean {
        return this.isAlarmDefVisible()
            && this.isAlarmDefReferenceValueTypeCustomValue()
    }

    private alarmDefReferenceValueTypeSwitchExists(): boolean {
        return this.alarmDefReferenceValueTypeSwitch != null
    }

    private isAlarmDefReferenceValueTypeCustomValue(): boolean {
        return this.alarmDefReferenceValueTypeSwitchExists()
            && this.alarmDefReferenceValueTypeSwitch.option(AlarmDefinitionItemEditController.ValuePropertyName) == true
    }

    private isAlarmDefReferenceValueBoolean(): boolean {
        return this.alarmDefExists()
            && this.options.alarmDef.alarmDef.dataTypeAlarm != null
            && this.options.alarmDef.alarmDef.dataTypeAlarm.idDataTypeClass == Enums.DataTypeClass.Boolean
    }

    private alarmDefUnitExists(): boolean {
        return this.alarmDefExists()
            && this.options.alarmDef.alarmDef.dataTypeAlarm != null
            && this.options.alarmDef.alarmDef.dataTypeAlarm.unit != null
    }

    // #endregion

    // #region Validation rules

    private createValidationRuleRequired(revaluate: boolean = false): any {
        return {
            type: 'required',
            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField)
        }
    }

    // #endregion
}
